import { EFinancingCompany, EFinancingProgram, ELanguage } from '@if/core/types'
import LanguageIcon from '@mui/icons-material/Language'
import { IconButton, Link } from '@mui/material'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid'
import { Merchant } from '@src/data/types/Merchant'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

export function useSelectedCompanyPrograms(selectedCompany: EFinancingCompany): EFinancingProgram[] {
  return useMemo(() => {
    if (selectedCompany === EFinancingCompany.IFinance) {
      return [EFinancingProgram.Personal]
    }
    return [EFinancingProgram.Auto, EFinancingProgram.IfXpress, EFinancingProgram.Products, EFinancingProgram.MechanicalWarranty]
  }, [selectedCompany])
}

export function useGetProgramPlanOptionsByFinancingProgramId(financingProgramId: EFinancingProgram | undefined) {
  return useMemo(() => {
    switch (financingProgramId) {
      case EFinancingProgram.Personal:
        return ['*', '**', '***', '****', '*****']
      case EFinancingProgram.IfXpress:
        return ['*', '**', '***', '****', '*****']

      default:
        return []
    }
  }, [financingProgramId])
}
export function useMerchantColumns(territoriesById: Record<string, string>) {
  const { t } = useTranslation()

  const columns = useMemo<GridColDef<Merchant>[]>(
    () => [
      {
        field: 'id',
        headerName: '#',
        flex: 0.3,
        headerAlign: 'center',
        align: 'center',
        renderCell: (params: GridRenderCellParams<Merchant, string>) => {
          return (
            <Link href={`/merchant/${params.value}/view`} variant="inherit">
              {params.value?.split('*').join(' ')}
            </Link>
          )
        },
      },
      {
        field: 'name',
        headerName: t('common.fullName'),
        flex: 1,
      },
      {
        field: 'languageId',
        headerName: t('common.language'),
        align: 'center',
        headerAlign: 'center',
        valueFormatter: (value: ELanguage) => {
          return t(`ELanguage.${value}`, { ns: 'enum' })
        },
      },
      {
        field: 'province',
        headerName: 'Province',
        headerAlign: 'center',
        align: 'center',
      },
      {
        field: 'territoryId',
        headerName: t('common.territory'),
        headerAlign: 'center',
        align: 'center',
        flex: 0.5,
        sortable: true,
        valueFormatter: (value): string => {
          return territoriesById[value] || t('merchant.noTerritory')
        },
      },

      {
        field: 'supportedFinancingPrograms',
        headerName: t('common.program'),
        headerAlign: 'center',
        align: 'center',
        valueFormatter: (values: EFinancingProgram[]) => {
          return values?.map((value: EFinancingProgram) => t(`EFinancingProgram.${value}`, { ns: 'enum' }))
        },
      },
      {
        field: 'website',
        headerName: t('common.website'),
        renderCell: (params: GridRenderCellParams<Merchant, string>) => {
          return (
            <IconButton disabled={!params.value}>
              <Link href={params.value} target="_blank">
                <LanguageIcon color={!params.value ? 'disabled' : 'inherit'} />
              </Link>
            </IconButton>
          )
        },
      },
    ],
    [t, territoriesById],
  )

  return columns
}
